/**
 * The MEMBER-facing LOOP-INPUT (drafting) RPC definitions (OL-114).
 *
 * A person says what they want in prose; this group answers with a PROPOSAL — a
 * draft it understood, the fields it could not fill, and the ones it refuses to
 * guess. **It writes nothing, here and forever**: a second, explicit call is
 * what creates a definition or an invocation.
 *
 * **`customerId` on a payload here is a SELECTOR among the caller's own
 * memberships, never an authority.**
 *
 * ## The wire schemas below are FROZEN by the seam issue
 *
 * The extractor issue replaces the BODY of one function (`draftLoopInput` in
 * `loop-inputs.router.ts`) with a `generateObject` call and changes neither the
 * wire schema nor the registration. Two properties are what make that safe, and
 * both are spelled out mechanically rather than trusted to a later reader:
 *
 *  1. **Every field of {@link LoopInputDraftUnderstood} is `Schema.NullOr`,
 *     `trigger` included.** "The model did not answer this" therefore has
 *     exactly one spelling on the wire, and the extractor cannot widen or
 *     re-shape it.
 *  2. **{@link LoopInputDraftDefinition}`.inputSchema` is
 *     `Schema.Array(LoopInput)`, importing `LoopInput` from `@fm/db/models/Loop`**
 *     — the same symbol `loops.input_schema` is typed with and the same one the
 *     definition group's `loops.define` payload accepts. Declaring a second
 *     element shape here is the exact defect this freeze exists to prevent: a
 *     `"definition"` draft's `inputSchema` must be byte-for-byte acceptable to a
 *     define with no reshaping, and it is only that if both sides import one
 *     symbol.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { LoopInput } from "@fm/db/models/Loop";
import { Schema } from "effect";

import { TriggerSpecInput } from "../admin/orcaloops-outcomes.rpc.ts";
import { DatabaseError, Forbidden, Unauthorized, ValidationError } from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";

/**
 * What the person asked for, plus any answers they have already given to
 * earlier questions.
 *
 * @since 1.0.0
 */
export const LoopInputDraftRequest = Schema.Struct({
  prompt: Schema.String,
  /** Answers to a previous draft's `unanswered` questions, in order. */
  followUp: Schema.optional(Schema.Array(Schema.String)),
  customerId: Schema.optional(Schema.String),
});
export type LoopInputDraftRequest = Schema.Schema.Type<typeof LoopInputDraftRequest>;

/**
 * The seven execution facts a draft may carry. EVERY field is `Schema.NullOr`,
 * `trigger` included — see the freeze note in this file's header.
 *
 * @since 1.0.0
 */
export const LoopInputDraftUnderstood = Schema.Struct({
  repo: Schema.NullOr(Schema.String),
  ref: Schema.NullOr(Schema.String),
  acceptance: Schema.NullOr(Schema.String),
  budgetUsd: Schema.NullOr(Schema.Number),
  maxParallel: Schema.NullOr(Schema.Int),
  maxTurns: Schema.NullOr(Schema.Int),
  trigger: Schema.NullOr(TriggerSpecInput),
});
export type LoopInputDraftUnderstood = Schema.Schema.Type<typeof LoopInputDraftUnderstood>;

/**
 * The definition a `"definition"` draft proposes, shaped so it is accepted by
 * `loops.define` with no reshaping.
 *
 * `description` and `acceptanceTemplate` spell their nullability the same way
 * the model does (`Schema.NullOr`, never optional), and `inputSchema` is
 * `Schema.Array(LoopInput)` over the ONE input-element symbol.
 *
 * @since 1.0.0
 */
export const LoopInputDraftDefinition = Schema.Struct({
  name: Schema.String,
  slug: Schema.String,
  description: Schema.NullOr(Schema.String),
  statementTemplate: Schema.String,
  acceptanceTemplate: Schema.NullOr(Schema.String),
  inputSchema: Schema.Array(LoopInput),
});
export type LoopInputDraftDefinition = Schema.Schema.Type<typeof LoopInputDraftDefinition>;

/**
 * One thing the draft could not fill in, phrased as a question a person can
 * answer.
 *
 * @since 1.0.0
 */
export const LoopInputDraftQuestion = Schema.Struct({
  field: Schema.String,
  question: Schema.String,
});
export type LoopInputDraftQuestion = Schema.Schema.Type<typeof LoopInputDraftQuestion>;

/**
 * One thing the draft REFUSES to fill in — distinct from an unanswered
 * question, which is merely unknown.
 *
 * @since 1.0.0
 */
export const LoopInputDraftRefusal = Schema.Struct({
  field: Schema.String,
  message: Schema.String,
});
export type LoopInputDraftRefusal = Schema.Schema.Type<typeof LoopInputDraftRefusal>;

/**
 * The proposal itself. Creates nothing.
 *
 * `statement` is the submitted prompt VERBATIM — never summarised, never
 * reworded. A drafting surface that quietly rewrites what a person asked for is
 * worse than one that asks.
 *
 * @since 1.0.0
 */
export const LoopInputDraft = Schema.Struct({
  draftId: Schema.String,
  kind: Schema.Literal("invocation", "definition"),
  statement: Schema.String,
  title: Schema.String,
  understood: LoopInputDraftUnderstood,
  definition: Schema.NullOr(LoopInputDraftDefinition),
  firstInvocationInputs: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.String })),
  unanswered: Schema.Array(LoopInputDraftQuestion),
  refusals: Schema.Array(LoopInputDraftRefusal),
});
export type LoopInputDraft = Schema.Schema.Type<typeof LoopInputDraft>;

/**
 * The member-facing drafting RPC group. Every member carries
 * {@link LoopActorMiddleware}.
 *
 * @since 1.0.0
 */
export class LoopInputRpcs extends RpcGroup.make(
  Rpc.make("loopInputs.draft", {
    payload: LoopInputDraftRequest,
    success: LoopInputDraft,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
) {}
